import { Hero, Link, Tag } from "@liberetech/design-system";
import Prose from "@liberetech/design-system/Prose";
import classNames from "classnames";
import { useState } from "react";

import { useBrand } from "components/Context/BrandContext";
import { useOnlineReception } from "components/Context/OnlineReceptionContext";
import { Page } from "components/Context/PageContext";
import { Seo, ArticleJsonLd, LanguageAlternates } from "components/Seo/Seo";
import fetchCms from "lib/client/cms";
import serverSideTranslations from "lib/i18n/_pagesRouterCompat/serverSideTranslations";
import { Trans, useTranslation } from "lib/i18n/client";
import { TrackingContextPage } from "lib/tracking";
import withDirectChannelCommonProps from "lib/withDirectChannelCommonProps";

import styles from "./[slug].module.css";

const FAQPost: React.FC<FAQPostProps> = ({ faq }) => {
  const { t } = useTranslation("faqs");
  const brand = useBrand();

  return (
    <>
      <Page
        title={faq.title}
        backHref={`/${t("common:routes.onlineReception")}`}
      />
      <Seo
        title={`${faq.title} | ${t("title")}`}
        description={faq.description ?? undefined}
      />
      <Hero
        title={faq.title}
        titleInMobile
        breadcrumbs={[
          {
            title: t("common:breadcrumbs.home"),
            path: "/",
          },
          {
            title: t("breadcrumbs.faqs"),
            path: `/${t("common:routes.onlineReception")}`,
          },
        ]}
      />
      <div className={classNames("grid")}>
        <div className={styles.post}>
          <Prose>
            <div dangerouslySetInnerHTML={{ __html: faq.content }} />
          </Prose>
        </div>
      </div>
      <SectionFAQFeedback />
      <ArticleJsonLd
        title={faq.title}
        description={faq.description ?? ""}
        images={[]}
        datePublished={faq.createdAt}
        dateModified={faq.modifiedAt}
        authorName={brand.name}
        publisherName={brand.name}
        publisherLogo=""
        url=""
      />
    </>
  );
};

const SectionFAQFeedback: React.FC = () => {
  const { t } = useTranslation("faqs");
  const [feedbackSubmission, setFeedbackSubmission] = useState<
    "pending" | "good" | "bad"
  >("pending");

  return (
    <section className={"grid"}>
      <div className={styles.feedback}>
        <h6 className={styles.feedbackTitle}>{t("feedback.title")}</h6>
        {feedbackSubmission === "pending" && (
          <div className={styles.feedbackActions}>
            <button
              onClick={() => {
                setFeedbackSubmission("good");
                trackClick("feedback_ok");
              }}
            >
              <Tag
                icon={
                  <svg viewBox="0 0 32 32">
                    <path d="m9.145 25.023 2.59 1.296c1.295.609 2.743.99 4.267.99h7.619a2.694 2.694 0 0 0 2.666-2.286l1.143-9.143c0-1.295-.99-2.285-2.285-2.285h-6.096v-3.81c0-2.21-1.371-4.495-3.58-5.18-.458-.153-.991.228-.991.761v4.42l-5.333 6.856" />
                    <path d="M9.144 14.355H4.572v12.953h4.572V14.355Z" />
                  </svg>
                }
              >
                {t("feedback.yes")}
              </Tag>
            </button>
            <button
              onClick={() => {
                setFeedbackSubmission("bad");
                trackClick("feedback_ko");
              }}
            >
              <Tag
                icon={
                  <svg viewBox="0 0 32 32">
                    <path d="m22.857 6.856-2.59-1.296c-1.295-.61-2.743-.99-4.267-.99H8.381a2.694 2.694 0 0 0-2.666 2.286l-1.143 9.143c0 1.295.99 2.285 2.285 2.285h6.096v3.81c0 2.21 1.371 4.495 3.58 5.18.458.153.991-.228.991-.761v-4.42l5.333-6.856" />
                    <path d="M22.858 17.523h4.572V4.571h-4.572v12.952Z" />
                  </svg>
                }
              >
                {t("feedback.no")}
              </Tag>
            </button>
          </div>
        )}
        {feedbackSubmission === "good" && (
          <p className={styles.feedbackSubmitted}>
            {t("feedback.yesSubmitted")}
          </p>
        )}
        {feedbackSubmission === "bad" && (
          <p className={styles.feedbackSubmitted}>
            <Trans
              i18nKey="faqs:feedback.noSubmitted"
              components={{ contactLink: <ContactLink /> }}
            />
          </p>
        )}
      </div>
    </section>
  );
};

const ContactLink: React.FC = ({ children }) => {
  const onlineReception = useOnlineReception();
  return (
    <button
      onClick={() => {
        onlineReception.open();
        window.dataLayer.push({
          event: "open",
          event_category: "online_reception",
          event_label: "faq_feedback",
        });
      }}
    >
      <Link>{children}</Link>
    </button>
  );
};

const trackClick = (label: string) => {
  window.dataLayer.push({
    event: "click",
    event_category: "faq",
    event_label: label,
  });
};

export const getServerSideProps = withDirectChannelCommonProps<
  FAQPostProps,
  FAQPostParams
>(async ({ req, params, locale }) => {
  if (!params || !params.slug || Array.isArray(params.slug) || !locale) {
    return {
      notFound: true,
    };
  }

  const response = await fetchCms(
    `{
      faq(id: "/${locale}/faq/${params.slug}" idType: URI) {
        databaseId
        title
        slug
        excerpt
        content
        date
        modified
        uri
        seo {
          metaDesc
          opengraphImage {
            sourceUrl
          }  
        }
        translations {
          uri
          slug
          language {
            slug
          }
        }
      }
    }`,
    req.context.brand.cmsUrl,
  );

  if (!response || !response.faq) {
    return {
      notFound: true,
    };
  }

  const faq = response.faq as {
    databaseId: string;
    title: string;
    slug: string;
    excerpt: string;
    content: string;
    date: string;
    modified: string;
    uri: string;
    seo: { metaDesc: string };
    translations: Array<{
      slug: string;
      uri: string;
      language: { slug: string };
    }>;
  };

  return {
    props: {
      ...(await serverSideTranslations("/faqs/[slug]", locale)),
      faq: {
        title: faq.title,
        description: faq.seo.metaDesc || faq.excerpt,
        slug: faq.slug,
        content: faq.content,
        createdAt: faq.date,
        modifiedAt: faq.modified,
        uri: faq.uri,
      },
      trackingContext: {
        page_type: "faq_post",
        faq_id: faq.databaseId,
      },
      languageAlternates: [
        {
          hrefLang: locale,
          href: `/${faq.uri}`,
        },
        ...faq.translations.reduce((prev: LanguageAlternates, translation) => {
          if (!translation.uri || !translation.language.slug) {
            return prev;
          }
          return [
            ...prev,
            { hrefLang: translation.language.slug, href: translation.uri },
          ];
        }, []),
      ],
    },
  };
});

type FAQPostProps = {
  faq: {
    title: string;
    description: string;
    slug: string;
    content: string;
    createdAt: string;
    modifiedAt: string;
    uri: string;
  };
  trackingContext: TrackingContextPage;
};

type FAQPostParams = {
  slug: string;
};

export default FAQPost;
